import React from "react";
import {
  FieldWrapper,
  FilterMultiSelect,
  FilterToggle,
  FilterSortGroup,
  formatGroupedValues,
} from "./jobsFilterUtils";

export const SETTINGS_SORT_OPTIONS = [
  { value: "name", label: "Name" },
];

export const APP_NOTES_STATUS_OPTIONS = [
  { value: "checkIn", label: "Check In" },
  { value: "checkInEarly", label: "Check In Early" },
  { value: "checkOut", label: "Check Out" },
  { value: "checkOutLate", label: "Check Out Late" },
];

// 1. SKILLS
export function AdminSettingsSkillsFilterFields({
  focusedFieldId,
  onFocusClear,
  divisionFilter = [],
  setDivisionFilter,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  divisions = [],
}) {
  const divisionOptions = divisions.map((d) => ({
    value: String(d.division_id ?? d.id),
    label: d.name,
  }));

  return (
    <>
      <FieldWrapper
        fieldId="division"
        label="Division"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="admin-settings-skills-division"
          options={divisionOptions}
          value={divisionFilter.map(String)}
          onChange={(vals) => setDivisionFilter(vals)}
          placeholder="Select Division..."
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="showDeleted"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterToggle
          id="admin-settings-skills-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-settings-skills-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={SETTINGS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminSettingsSkillsFilterChips({
  divisionFilter = [],
  showDeleted,
  sortDir,
  divisions = [],
}) {
  const chips = [];
  if (divisionFilter.length > 0) {
    const formatDiv = (id) => divisions.find((d) => String(d.division_id ?? d.id) === String(id))?.name || id;
    chips.push({
      id: "division",
      key: "divisionFilter",
      fieldId: "division",
      label: `Divisions: ${formatGroupedValues(divisionFilter, formatDiv, 3)}`,
    });
  }
  if (showDeleted) {
    chips.push({
      id: "showDeleted",
      key: "showDeleted",
      fieldId: "showDeleted",
      label: "Show Deleted",
    });
  }
  if (sortDir !== "asc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// 2. CERTIFICATIONS
export function AdminSettingsCertificationsFilterFields({
  focusedFieldId,
  onFocusClear,
  divisionFilter = [],
  setDivisionFilter,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  divisions = [],
}) {
  return (
    <AdminSettingsSkillsFilterFields
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
      divisionFilter={divisionFilter}
      setDivisionFilter={setDivisionFilter}
      showDeleted={showDeleted}
      setShowDeleted={setShowDeleted}
      sortKey={sortKey}
      setSortKey={setSortKey}
      sortDir={sortDir}
      setSortDir={setSortDir}
      divisions={divisions}
    />
  );
}

export const buildAdminSettingsCertificationsFilterChips = buildAdminSettingsSkillsFilterChips;

// 3. POSITIONS
export function AdminSettingsPositionsFilterFields({
  focusedFieldId,
  onFocusClear,
  divisionFilter = [],
  setDivisionFilter,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  divisions = [],
}) {
  return (
    <AdminSettingsSkillsFilterFields
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
      divisionFilter={divisionFilter}
      setDivisionFilter={setDivisionFilter}
      showDeleted={showDeleted}
      setShowDeleted={setShowDeleted}
      sortKey={sortKey}
      setSortKey={setSortKey}
      sortDir={sortDir}
      setSortDir={setSortDir}
      divisions={divisions}
    />
  );
}

export const buildAdminSettingsPositionsFilterChips = buildAdminSettingsSkillsFilterChips;

// 4. DIVISIONS
export function AdminSettingsDivisionsFilterFields({
  focusedFieldId,
  onFocusClear,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="showDeleted"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterToggle
          id="admin-settings-divisions-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-settings-divisions-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={SETTINGS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminSettingsDivisionsFilterChips({
  showDeleted,
  sortDir,
}) {
  const chips = [];
  if (showDeleted) {
    chips.push({
      id: "showDeleted",
      key: "showDeleted",
      fieldId: "showDeleted",
      label: "Show Deleted",
    });
  }
  if (sortDir !== "asc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// 5. OFFICES
export function AdminSettingsOfficesFilterFields({
  focusedFieldId,
  onFocusClear,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <AdminSettingsDivisionsFilterFields
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
      showDeleted={showDeleted}
      setShowDeleted={setShowDeleted}
      sortKey={sortKey}
      setSortKey={setSortKey}
      sortDir={sortDir}
      setSortDir={setSortDir}
    />
  );
}

export const buildAdminSettingsOfficesFilterChips = buildAdminSettingsDivisionsFilterChips;

// 6. APP NOTES
export function AdminSettingsAppNotesFilterFields({
  focusedFieldId,
  onFocusClear,
  selectedSigninStatuses = [],
  setSelectedSigninStatuses,
  showDeleted,
  setShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="status"
        label="Status"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="admin-settings-appnotes-status"
          options={APP_NOTES_STATUS_OPTIONS}
          value={selectedSigninStatuses}
          onChange={setSelectedSigninStatuses}
          placeholder="Select Status..."
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="showDeleted"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterToggle
          id="admin-settings-appnotes-deleted"
          label="Show Deleted"
          checked={showDeleted}
          onChange={setShowDeleted}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-settings-appnotes-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={SETTINGS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminSettingsAppNotesFilterChips({
  selectedSigninStatuses = [],
  showDeleted,
  sortDir,
}) {
  const chips = [];
  if (selectedSigninStatuses.length > 0) {
    const formatStatus = (val) => APP_NOTES_STATUS_OPTIONS.find((o) => o.value === val)?.label || val;
    chips.push({
      id: "status",
      key: "selectedSigninStatuses",
      fieldId: "status",
      label: `Statuses: ${formatGroupedValues(selectedSigninStatuses, formatStatus, 3)}`,
    });
  }
  if (showDeleted) {
    chips.push({
      id: "showDeleted",
      key: "showDeleted",
      fieldId: "showDeleted",
      label: "Show Deleted",
    });
  }
  if (sortDir !== "asc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}
